import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { EquineService } from '../../equine.service';
import { QuoteService } from '../../../quote.service';

const CLAIM_STATUS_LABELS: Record<number, string> = {
  0: 'Accepted (complete)',
  1: 'Accepted (ongoing)',
  2: 'Declined'
};

@Component({
  selector: 'app-equine-quote-full-details',
  templateUrl: './equine-quote-full-details.component.html',
  styleUrls: ['./equine-quote-full-details.component.css']
})
export class EquineQuoteFullDetailsComponent implements OnChanges {
  private static readonly TRANSACTION_LABELS: Record<number, string> = {
    1: 'New Business',
    2: 'Renewal',
    3: 'MTA',
    4: 'Cancellation',
    5: 'Lapsed',
    6: 'NTU',
  };

  public isStandardCover: boolean = false;
  public isRestrictedCover: boolean = false;
  public isBloodstockCover: boolean = false;
  public isACECover: boolean = false;

  @Input() quoteDetails: any = {};
  @Input() staticData: any = null;

  /** Map claim type UUID -> name for display (loaded from API). */
  claimTypesMap: Record<string, string> = {};
  /** Main use of horse display name (set when class-of-use options load). */
  mainUseOfHorseDisplay: string = '—';
  /** Vet fee option id -> display label. */
  vetFeeOptionsMap: Record<string, string> = {};
  /** Vet fee excess id -> display label. */
  vetFeeExcessOptionsMap: Record<string, string> = {};
  /** Vet fee co-insurance id -> display label. */
  vetFeeCoInsuranceMap: Record<string, string> = {};

  private static readonly ALL_VET_FEE_OPTIONS: Record<string, string> = {
    '1vfcac3f-b4a7-431e-9436-8d03cec4egth': '£3000 per incident (£6000 aggregate)',
    '2vfcac3f-b4a7-431e-9436-8d03cec4egth': '£4500 per incident (£9000 aggregate)',
    '3vfcac3f-b4a7-431e-9436-8d03cec4egth': '£6000 per incident (£12,000 aggregate)',
    '4vfcac3f-b4a7-431e-9436-8d03cec4egth': '£7500 per incident (£15,000 aggregate)',
    '5vfcac3f-b4a7-431e-9436-8d03cec4egth': '£5500 per annum',
    '6vfcac3f-b4a7-431e-9436-8d03cec4egth': '£3500 per annum',
    '7vfcac3f-b4a7-431e-9436-8d03cec4egth': '£5000 per annum',
    '8vfcac3f-b4a7-431e-9436-8d03cec4egth': '£7500 per annum',
    '9vfcac3f-b4a7-431e-9436-8d03cec4egth': '£7500 per annum'
  };
  private static readonly ALL_VET_FEE_EXCESS_OPTIONS: Record<string, string> = {
    '1vfxac3f-b4a7-431e-9436-8d03cec4er56': '£150',
    '2vfxac3f-b4a7-431e-9436-8d03cec4er56': '£250',
    '3vfxac3f-b4a7-431e-9436-8d03cec4er56': '£375',
    '4vfxac3f-b4a7-431e-9436-8d03cec4er56': '£500',
    '5vfxac3f-b4a7-431e-9436-8d03cec4er56': '£175 + 20%',
    '6vfxac3f-b4a7-431e-9436-8d03cec4er56': '£375 + 20%',
    '7vfxac3f-b4a7-431e-9436-8d03cec4er56': '£500',
    '8vfxac3f-b4a7-431e-9436-8d03cec4er56': '£200',
    '9vfxac3f-b4a7-431e-9436-8d03cec4er56': '£350',
    '10vfxa3f-b4a7-431e-9436-8d03cec4er56': '£500',
    '11vfxa3f-b4a7-431e-9436-8d03cec4er56': '£350',
    '12vfxa3f-b4a7-431e-9436-8d03cec4er56': '£350'
  };
  private static readonly ALL_VET_FEE_CO_INSURANCE_OPTIONS: Record<string, string> = {
    '1vfcic3f-b4a7-431e-9436-8d03cec4e3r5': '0%',
    '2vfcic3f-b4a7-431e-9436-8d03cec4e3r5': '15%',
    '3vfcic3f-b4a7-431e-9436-8d03cec4e3r5': '20%',
    '4vfcic3f-b4a7-431e-9436-8d03cec4e3r5': '25%',
    '5vfcic3f-b4a7-431e-9436-8d03cec4e3r5': '30%'
  };

  private endorsementsLoadedForId: string = '';
  endorsementQuote: any = {};

  generalSofList: any[] = [];
  veterinarySofList: any[] = [];
  declarationSofList: any[] = [];
  private sofQuestionsMap: Record<string, any> = {};

  constructor(private equineService: EquineService, private quoteService: QuoteService) {}

  get transactionTypeLabel(): string {
    const txType = Number(this.quoteDetails?.transaction_type) || 0;
    return EquineQuoteFullDetailsComponent.TRANSACTION_LABELS[txType] || String(txType);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (this.quoteDetails && this.staticData) {
      this.mainUseOfHorseDisplay = this.quoteDetails.main_use_name ?? '—';
      this.quoteDetails.horse_class_name = this.getClassOfUse();
      this.loadClaimTypes();
      this.loadVetFeeOptionsIfNeeded();
      this.loadCategorisedSof();
      this.updateCoverTypeFlags();
    }
    if (this.quoteDetails?.id) {
      const prevId = this.endorsementQuote?.id;
      const prevEndorsements = Array.isArray(this.endorsementQuote?.endorsements)
        ? this.endorsementQuote.endorsements
        : undefined;
      const incomingEndorsements = Array.isArray(this.quoteDetails?.endorsements)
        ? this.quoteDetails.endorsements
        : undefined;
      const willReload = this.quoteDetails.id !== this.endorsementsLoadedForId;
      // Discount/loading saves refresh quoteDetails without endorsements. Keep any known list
      // for the same quote, and re-fetch when the payload omitted them.
      this.endorsementQuote = { ...this.quoteDetails };
      if (incomingEndorsements) {
        this.endorsementQuote.endorsements = incomingEndorsements;
      } else if (prevId === this.quoteDetails.id && prevEndorsements?.length) {
        this.endorsementQuote.endorsements = prevEndorsements;
      }
      const shouldRefetch = willReload || incomingEndorsements === undefined;
      if (shouldRefetch) {
        this.loadEndorsements();
      }
    }
  }

  private loadEndorsements(): void {
    const id = this.quoteDetails.id;
    this.endorsementsLoadedForId = id;
    this.quoteService.getEndorsement(id).subscribe(
      (res: any) => {
        if (res?.success && Array.isArray(res.result)) {
          this.endorsementQuote = { ...this.quoteDetails, endorsements: res.result };
        }
      }
    );
  }

  /** Populate vet fee, excess, and co-insurance display maps from hardcoded UUIDs and API. */
  private loadVetFeeOptionsIfNeeded(): void {
    const q = this.quoteDetails;
    this.vetFeeOptionsMap = { ...EquineQuoteFullDetailsComponent.ALL_VET_FEE_OPTIONS };
    this.vetFeeExcessOptionsMap = { ...EquineQuoteFullDetailsComponent.ALL_VET_FEE_EXCESS_OPTIONS };
    this.vetFeeCoInsuranceMap = { ...EquineQuoteFullDetailsComponent.ALL_VET_FEE_CO_INSURANCE_OPTIONS };

    if (!q || q.is_vet_fee_selected != 1) return;

    const typeId = q.vet_fee_cover_type ?? q.vet_fee_type_id;
    if (!typeId || typeId === '' || typeId === 0 || typeId === '0') return;

    this.equineService.getVetFeeDetails(typeId).subscribe((res: any) => {
      if (!res?.success || !res.result) return;
      const vetFeeList = res.result.vet_fee;
      if (Array.isArray(vetFeeList)) {
        vetFeeList.forEach((o: any) => {
          const id = o.id != null ? String(o.id) : '';
          if (id && o.option_name) this.vetFeeOptionsMap[id] = o.option_name;
        });
      }
      const excessList = res.result.excess;
      if (Array.isArray(excessList)) {
        excessList.forEach((o: any) => {
          const id = o.id != null ? String(o.id) : '';
          if (id && o.option_name) this.vetFeeExcessOptionsMap[id] = o.option_name;
        });
      }
      const coInsList = res.result.co_insurance;
      if (Array.isArray(coInsList)) {
        coInsList.forEach((o: any) => {
          const id = o.id != null ? String(o.id) : '';
          if (id && o.option_name) this.vetFeeCoInsuranceMap[id] = o.option_name;
        });
      }
    });
  }

  private loadCategorisedSof(): void {
    const q = this.quoteDetails;
    const sofAnswers: any[] = Array.isArray(q?.statement_of_facts) ? q.statement_of_facts : [];
    const coverTypeId = q?.policy_cover_details?.cover_type_id ?? q?.cover_type_id;
    const isLossOfHorse = Number(q?.policy_cover_details?.is_loss_of_horse_selected ?? q?.is_loss_of_horse_selected ?? 0);
    const isMort = isLossOfHorse === 1 ? 1 : undefined;

    this.equineService.getStatementOfFactsByCoverType(coverTypeId, isMort).subscribe((res: any) => {
      if (!res?.success) return;
      const result = res.result;

      let generalQuestions: any[] = [];
      let vetQuestions: any[] = [];

      if (result && !Array.isArray(result) && (result.general_sof || result.veterinary_sof)) {
        generalQuestions = Array.isArray(result.general_sof) ? result.general_sof : [];
        vetQuestions = Array.isArray(result.veterinary_sof) ? result.veterinary_sof : [];
      } else {
        const list = Array.isArray(result) ? result : (result?.sof_ques ?? result?.statement_of_fact ?? []);
        vetQuestions = Array.isArray(list) ? list : [];
      }

      const declQuestions = generalQuestions.filter((q: any) => q.is_declaration == 1);
      generalQuestions = generalQuestions.filter((q: any) => q.is_declaration != 1);

      this.sofQuestionsMap = {};
      [...generalQuestions, ...vetQuestions, ...declQuestions].forEach((q: any) => {
        this.sofQuestionsMap[q.id] = q;
      });

      const generalIds = new Set(generalQuestions.map((q: any) => q.id));
      const vetIds = new Set(vetQuestions.map((q: any) => q.id));
      const declIds = new Set(declQuestions.map((q: any) => q.id));

      const sortByOrder = (a: any, b: any) => (a.display_order ?? 0) - (b.display_order ?? 0);

      this.generalSofList = sofAnswers.filter((s: any) => generalIds.has(s.question_id)).sort(sortByOrder);
      this.veterinarySofList = sofAnswers.filter((s: any) => vetIds.has(s.question_id)).sort(sortByOrder);
      this.declarationSofList = sofAnswers.filter((s: any) => declIds.has(s.question_id)).sort(sortByOrder);
    });
  }

  /** Load claim types (proposed horse + other horses) to resolve UUID to name. */
  private loadClaimTypes(): void {
    this.equineService.getClaimTypes(1).subscribe((res: any) => {
      if (res?.success && Array.isArray(res.result)) {
        res.result.forEach((t: any) => { this.claimTypesMap[t.id] = t.name || ''; });
      }
    });
    this.equineService.getClaimTypes(2).subscribe((res: any) => {
      if (res?.success && Array.isArray(res.result)) {
        res.result.forEach((t: any) => { this.claimTypesMap[t.id] = t.name || ''; });
      }
    });
  }

  private static readonly BLOODSTOCK_CLASS_ID = '6cudc37f-b4a7-431e-9436-8d03cecceac0';

  private getClassOfUse(): string {
    const list = this.staticData?.class_of_use;
    if (!list || !Array.isArray(list)) return '—';
    const classOfUse = list.find((c: any) => c.id === this.quoteDetails.horse_class_id);
    const className = classOfUse?.class_name || '—';

    const coverName = (this.getCoverTypeName() || '').toLowerCase();
    if (coverName.includes('bloodstock') && this.quoteDetails.blood_stock_id) {
      this.loadBloodstockActivityName();
    } else {
      const activities = this.staticData?.horse_activities;
      if (Array.isArray(activities) && this.quoteDetails.horse_class_sub_id) {
        const activity = activities.find((a: any) => a.id === this.quoteDetails.horse_class_sub_id);
        const name = activity ? (activity.name || activity.option_name || '—') : '—';
        this.quoteDetails.main_use_name = name;
        this.mainUseOfHorseDisplay = name;
      } else {
        this.mainUseOfHorseDisplay =
          this.quoteDetails.horse_class_sub?.sub_class_name
          || this.quoteDetails.main_use_name
          || '—';
      }
    }
    return className;
  }

  private loadBloodstockActivityName(): void {
    this.equineService.getClassActivities(EquineQuoteFullDetailsComponent.BLOODSTOCK_CLASS_ID).subscribe({
      next: (res: any) => {
        const list = res?.result ?? res?.data ?? [];
        if (Array.isArray(list)) {
          const match = list.find((o: any) =>
            o.id === this.quoteDetails.blood_stock_id || String(o.id) === String(this.quoteDetails.blood_stock_id)
          );
          if (match) {
            const name = match.name || match.option_name || '—';
            this.mainUseOfHorseDisplay = name;
            this.quoteDetails.main_use_name = name;
          }
        }
      }
    });
  }

  /** Resolve claim type UUID to display name. */
  getClaimTypeName(claim: any): string {
    if (!claim) return '—';
    const id = claim.claim_type_id ?? claim.claim_type;
    if (id == null || id === '') return '—';
    return this.claimTypesMap[id] ?? claim.claim_type_name ?? id;
  }

  /** Formatted proposer/correspondence address (single line). */
  get proposerAddressLine(): string {
    const pd = this.quoteDetails?.proposer_details;
    if (!pd) return '—';
    const addr = pd.address;
    if (!addr || typeof addr !== 'object') return '—';
    const a1 = addr.address1 ?? addr.address_1 ?? '';
    const a2 = addr.address2 ?? addr.address_2 ?? '';
    const city = addr.city ?? addr.town_county ?? '';
    const postcode = addr.postcode ?? '';
    const parts = [a1, a2, city, postcode].filter(Boolean);
    return parts.length ? parts.join(', ') : '—';
  }

  /** Claims for the proposed horse (horse_type 1). */
  get proposedHorseClaims(): any[] {
    const list = this.quoteDetails?.claims_history;
    if (!Array.isArray(list)) return [];
    return list.filter((c: any) => c.horse_type === 1 || c.horse_type === '1');
  }

  /** Claims for other horses (horse_type 2). */
  get otherHorsesClaimsList(): any[] {
    const list = this.quoteDetails?.claims_history;
    if (!Array.isArray(list)) return [];
    return list.filter((c: any) => c.horse_type === 2 || c.horse_type === '2');
  }

  getClaimStatusLabel(status: number | string | null | undefined): string {
    if (status == null || status === '') return '—';
    const n = Number(status);
    return CLAIM_STATUS_LABELS[n] ?? String(status);
  }

  /** Statement of fact response as Yes/No or response_text. */
  getStatementOfFactResponse(sof: any): string {
    if (!sof) return 'N/A';
    if (sof.response == 1) return 'Yes';
    if (sof.response == 2) return 'No';

    const text = (sof.response_text || '').trim();
    return text || 'N/A';
  }

  public getIsHorseVettedDisplay(): string {
    if (!this.quoteDetails.horse_details.is_horse_vetted) return 'N/A';
    return this.quoteDetails.horse_details.is_horse_vetted == 2 ? 'Yes' : 'No';
  }

  public getCurrentlyInsuredDisplay(): string {
    if (!this.quoteDetails.horse_details.is_insured_by_you) return 'N/A';
    return this.quoteDetails.horse_details.is_insured_by_you == 2 ? 'Yes' : 'No';
  }

  /** Horse name for SOF placeholder replacement. */
  private getHorseNameForSof(): string {
    const hd = this.quoteDetails?.horse_details;
    return hd?.name.trim();
  }

  /** Policy holder full name for SOF placeholder replacement. */
  private getPolicyHolderNameForSof(): string {
    const pd = this.quoteDetails?.proposer_details;
    const first = (pd?.first_name ?? pd?.forename ?? '').trim();
    const last = (pd?.last_name ?? pd?.surname ?? '').trim();
    return `${first} ${last}`.trim() || 'the policy holder';
  }

  /** Replace [HORSENAME] / [HORSE_NAME] and [POLICY_HOLDER_NAME] in SOF text. */
  formatStatementOfFactText(text: string): string {
    if (!text || typeof text !== 'string') return '';
    const horseName = this.getHorseNameForSof();
    const policyHolderName = this.getPolicyHolderNameForSof();
    return text
      .replace(/\[HORSENAME\]/gi, horseName)
      .replace(/\[HORSE_NAME\]/gi, horseName)
      .replace(/\[POLICY_HOLDER_NAME\]/gi, policyHolderName)
      .replace(/\[POLICYHOLDERNAME\]/gi, policyHolderName)
      .trim();
  }

  /** Description for statement of fact (from staticData or loaded SOF questions), with placeholders replaced. */
  public getStatementOfFactDescription(sof: any): string {
    if (!sof) return '—';
    const fromMap = this.sofQuestionsMap[sof.question_id];
    const fromStatic = this.staticData?.sof_ques?.find((q: any) => q.id === sof.question_id);
    const question = fromMap || fromStatic;
    const raw = question?.description ?? sof.description ?? '—';
    return this.formatStatementOfFactText(raw) || '—';
  }

  /** Resolve ownership status ID to display name from staticData. */
  getOwnershipStatusName(id: string | number | null | undefined): string {
    if (id == null || id === '') return '';
    const list = this.staticData?.ownership_statuses;
    if (!Array.isArray(list)) return '';
    const item = list.find((s: any) => s.id === id || String(s.id) === String(id));
    return item ? (item.status_name ?? '') : '';
  }

  /** Resolve breed ID to display name from staticData. */
  getBreedName(id: string | number | null | undefined): string {
    if (id == null || id === '') return '';
    const list = this.staticData?.horse_breeds;
    if (!Array.isArray(list)) return '';
    const item = list.find((b: any) => b.id === id || String(b.id) === String(id));
    return item ? (item.breed ?? '') : '';
  }

  /** Resolve colour ID to display name from staticData. */
  getColorName(id: string | number | null | undefined): string {
    if (id == null || id === '') return '';
    const list = this.staticData?.horse_colors;
    if (!Array.isArray(list)) return '';
    const item = list.find((c: any) => c.id === id || String(c.id) === String(id));
    return item ? (item.color ?? '') : '';
  }

  getVetFeeCoverDisplay(): string {
    const q = this.quoteDetails;
    if (!q) return '—';
    if (typeof q.vet_fee === 'string' && q.vet_fee.trim()) return q.vet_fee;
    if (q.vet_fee?.option_name) return q.vet_fee.option_name;
    const id = q.vet_fee_id != null ? String(q.vet_fee_id) : '';
    if (id && this.vetFeeOptionsMap[id]) return this.vetFeeOptionsMap[id];
    return id || '—';
  }

  getVetFeeExcessDisplay(): string {
    const q = this.quoteDetails;
    if (!q) return '—';
    const id = q.vet_fee_excess_id != null ? String(q.vet_fee_excess_id) : '';
    const hardcoded = id ? EquineQuoteFullDetailsComponent.ALL_VET_FEE_EXCESS_OPTIONS[id] : null;
    if (hardcoded) return hardcoded;
    if (id && this.vetFeeExcessOptionsMap[id]) return this.vetFeeExcessOptionsMap[id];
    if (typeof q.vet_fee_excess === 'string' && q.vet_fee_excess.trim()) return q.vet_fee_excess;
    if (q.vet_fee_excess?.option_name) return q.vet_fee_excess.option_name;
    return id || '—';
  }

  getSumInsuredDisplay(): string {
    const q = this.quoteDetails;
    if (!q) return '—';
    const raw = q.sum_insured ?? q.financial_details?.sum_insured ?? q.horse_details?.sum_insured;
    if (raw == null || raw === '') return '—';
    const num = Number(raw);
    if (isNaN(num)) return String(raw);
    return num.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  getGenderName(): string {
    const hd = this.quoteDetails?.horse_details;
    if (!hd) return '—';
    if (hd.horse_gender?.title) return hd.horse_gender.title;
    const genderId = hd.gender_id;
    if (genderId) {
      const list = this.staticData?.genders;
      if (Array.isArray(list)) {
        const match = list.find((g: any) => g.id === genderId || String(g.id) === String(genderId));
        if (match?.title) return match.title;
      }
    }
    return '—';
  }

  getRawCoverTypeName(): string {
    const id = this.quoteDetails?.cover_type_id ?? this.quoteDetails?.policy_cover_details?.cover_type_id;
    const list = this.staticData?.cover_types;
    if (!id || !Array.isArray(list)) return '';
    const match = list.find((t: any) => t.id === id || String(t.id) === String(id));
    return (match?.cover_type_name ?? '').toLowerCase();
  }

  private updateCoverTypeFlags(): void {
    const raw = this.getRawCoverTypeName();
    this.isBloodstockCover = raw.includes('bloodstock');
    this.isRestrictedCover = raw.includes('restricted');
    this.isACECover = raw.includes('ace');
    this.isStandardCover = !!raw && !this.isBloodstockCover && !this.isRestrictedCover && !this.isACECover;
  }

  isYes(value: unknown): boolean {
    return value === 1 || value === '1';
  }

  getYesNoNotAvailable(applicable: boolean, selected: boolean): string {
    if (!applicable) return 'Not Available';
    return selected ? 'Yes' : 'No';
  }

  private getHorseAgeForLou(): number | null {
    const raw = this.quoteDetails?.horse_details?.age;
    if (raw == null || raw === '') return null;
    if (raw === '<1') return 0;
    const n = Number(raw);
    return Number.isNaN(n) ? null : n;
  }

  isLouEligibleForDisplay(): boolean {
    // horse age
    const age = this.getHorseAgeForLou();
    if (age == null || age < 4 || age > 12) return false;
    // class of use id
    const couId = this.quoteDetails?.horse_class_id ?? this.quoteDetails?.policy_cover_details?.class_of_use_id;
    if (!couId) return false;
    const list = this.staticData?.class_of_use;
    if (!Array.isArray(list)) return false;
    const selectedClass = list.find((c: any) => c.id === couId || String(c.id) === String(couId));
    return !!selectedClass && ['Class 2', 'Class 3', 'Class 4'].includes(selectedClass.class_name);
  }

  isLossOfHorseApplicable(): boolean {
    return true;
  }

  getLossOfHorseDisplay(): string {
    if (this.isACECover) {
      return this.isYes(this.quoteDetails?.is_loss_of_horse_selected) ? 'Yes' : 'No';
    }
    return 'Yes';
  }

  isElectiveSurgeryApplicable(): boolean {
    if (this.isBloodstockCover || this.isRestrictedCover) return false;
    return this.isACECover || this.isStandardCover;
  }

  getElectiveSurgeryDisplay(): string {
    return this.getYesNoNotAvailable(
      this.isElectiveSurgeryApplicable(),
      this.isYes(this.quoteDetails?.is_elective_surgery)
    );
  }

  isDisposalApplicable(): boolean {
    if (this.isBloodstockCover) return false;
    return this.isACECover || this.isStandardCover || this.isRestrictedCover;
  }

  getDisposalDisplay(): string {
    return this.getYesNoNotAvailable(
      this.isDisposalApplicable(),
      this.isYes(this.quoteDetails?.is_disposal_euthansia)
    );
  }

  private isStandardEssentialCover(): boolean {
    const q = this.quoteDetails;
    if (!this.isStandardCover || this.isYes(q?.is_cover_for_permanent_lou)) return false;
    return this.isYes(q?.is_vet_fee_selected) && Number(q?.vet_fee_cover_type) === 2;
  }

  isPermanentLouApplicable(): boolean {
    if (this.isStandardEssentialCover()) return false;
    return this.isStandardCover && this.isLouEligibleForDisplay();
  }

  getPermanentLouDisplay(): string {
    if (!this.isStandardCover || this.isStandardEssentialCover()) return 'Not Available';
    if (!this.isLouEligibleForDisplay()) return 'Not Available';
    return this.isYes(this.quoteDetails?.is_cover_for_permanent_lou) ? 'Yes' : 'No';
  }

  getVetFeeCoverSelectedDisplay(): string {
    if (this.isACECover) return 'Yes';
    return this.isYes(this.quoteDetails?.is_vet_fee_selected) ? 'Yes' : 'No';
  }

  isHospitalisationApplicable(): boolean {
    if (this.isBloodstockCover) return false;
    return this.isACECover || this.isStandardCover || this.isRestrictedCover;
  }

  getHospitalisationDisplay(): string {
    return this.getYesNoNotAvailable(
      this.isHospitalisationApplicable(),
      this.isYes(this.quoteDetails?.is_livery_hispitalization)
    );
  }

  isLifeSavingSurgeryApplicable(): boolean {
    if (this.isBloodstockCover || this.isACECover) return false;
    return (this.isStandardCover || this.isRestrictedCover);
  }

  getLifeSavingSurgeryDisplay(): string {
    return this.getYesNoNotAvailable(
      this.isLifeSavingSurgeryApplicable(),
      this.isYes(this.quoteDetails?.is_life_saving_surgery_selected)
    );
  }

  showLouActivityRow(): boolean {
    return this.isPermanentLouApplicable() && this.isYes(this.quoteDetails?.is_cover_for_permanent_lou) && !!this.quoteDetails?.lou_activity_id;
  }

  getCoverTypeName(): string {
    const id = this.quoteDetails?.cover_type_id;
    let name = '';
    const list = this.staticData?.cover_types;
    if (id && Array.isArray(list)) {
      const match = list.find((t: any) => t.id === id || String(t.id) === String(id));
      if (match) name = match.cover_type_name ?? '';
    }
    if (!name) return '—';
    const lower = name.toLowerCase();
    if (lower.includes('standard')) {
      const q = this.quoteDetails;
      const louSelected = Number(q?.is_cover_for_permanent_lou) === 1;
      const vetFeeSelected = q?.is_vet_fee_selected == 1;
      if (louSelected) return 'Standard - Enhanced';
      if (vetFeeSelected && q?.vet_fee_cover_type == 1) return 'Standard - Enhanced';
      if (vetFeeSelected && q?.vet_fee_cover_type == 2) return 'Standard - Essential';
      return 'Mortality Cover';
    }
    return name;
  }

  getVetFeeCoInsuranceDisplay(): string {
    const q = this.quoteDetails;
    if (!q) return '—';
    if (q.is_vet_fee_selected == 1 && Number(q.vet_fee_cover_type) === 2) return '—';
    if (typeof q.vet_fee_co_insurance === 'string' && q.vet_fee_co_insurance.trim()) return q.vet_fee_co_insurance;
    if (q.vet_fee_co_insurance?.option_name) return q.vet_fee_co_insurance.option_name;
    const id = q.vet_fee_co_insurance_id ? String(q.vet_fee_co_insurance_id) : '';
    if (id && this.vetFeeCoInsuranceMap[id]) return this.vetFeeCoInsuranceMap[id];
    return id || '—';
  }

  public getAgeDisplay(): string {
    if (!this.quoteDetails.horse_details.age) return 'N/A';
    return this.quoteDetails.horse_details.age == '<1' ? '<1' : Number(this.quoteDetails.horse_details.age).toString();
  }

  /**
   * Get the name of the LOU activity from the static data.
   * @returns 
   */
  public getLouActivityName(): string {
    if (!this.quoteDetails.lou_activity_id) return '—';
    const list = this.staticData?.lou_activities;
    if (!Array.isArray(list)) return '—';
    const item = list.find((a: any) => a.id === this.quoteDetails.lou_activity_id || String(a.id) === String(this.quoteDetails.lou_activity_id));
    return item ? (item.activity_name ?? '') : '—';
  }
}
